import CommentSection from "@/components/CommentSection";
import UserDetailComponent from "@/components/UserDetailComponent";
import { getFlagUrl } from "@/config/apiUrl";
import { POST_TYPE } from "@/constant/constants";
import {
  formatDateTime,
  formatNumberWithoutCurrency,
  getCountryISOCode,
  getPostQuestions,
  getPostSellerDetails,
} from "@/helper/HelperFunction";
import ImageSliderModal from "@/modals/ImageSliderModal";
import UsersModal from "@/modals/UsersModal";
import { useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { HiArrowDownTray, HiArrowUpTray } from "react-icons/hi2";
import AccordionComponent from "../../Accordion";
import MediaComponent from "../../MediaComponent";
import NoData from "../NoData/NoData";
import ShowMoreShowLessText from "../ShowMoreShowLess";
import classes from "./PostCard.module.css";

const ConsumerPostCard = ({ post = {} }) => {
  return (
    <>
      <div className={classes.postDetails}>
        <div className={classes.postDetailsLeft}>
          <UserDetailComponent
            data={{
              title: post?.[post?.type]?.plant?.name,
              description: post?.[post?.type]?.plant?.link,
            }}
            isPro={false}
            descType="link"
          />
          <div className={classes.postDetailsSeparator}>|</div>
          <div className={classes.postDetailsCountry}>
            <img
              src={getFlagUrl(getCountryISOCode(post?.[post?.type]?.country))}
              alt={post?.[post?.type]?.country}
            />
            <p>{post?.[post?.type]?.country}</p>
          </div>
        </div>

        <div className={classes.postDetailsRight}>
          <span>{post?.[post?.type]?.product?.name}</span>
          <p>
            {formatNumberWithoutCurrency(post?.[post?.type]?.capacity)}{" "}
            <span>{post?.[post?.type]?.unit}</span>
          </p>
        </div>
      </div>
      <AccordionComponent data={getPostQuestions(post)} />
      <AccordionComponent data={getPostSellerDetails(post)} />
    </>
  );
};

const ProducerPostCard = ({ post = {} }) => {
  return (
    <>
      <div className={classes.postDetails}>
        <div className={classes.postDetailsLeft}>
          <UserDetailComponent
            data={{
              title: post?.[post?.type]?.plant?.name,
              description: post?.[post?.type]?.plant?.link,
            }}
            isPro={false}
            descType="link"
          />
          <div className={classes.postDetailsSeparator}>|</div>
          <div className={classes.postDetailsCountry}>
            <img
              src={getFlagUrl(getCountryISOCode(post?.[post?.type]?.country))}
              alt={post?.[post?.type]?.country}
            />
            <p>{post?.[post?.type]?.country}</p>
          </div>
        </div>

        <div className={classes.postDetailsRight}>
          <span>{post?.[post?.type]?.product?.name}</span>
          <p>
            {formatNumberWithoutCurrency(post?.[post?.type]?.capacity)}{" "}
            <span>{post?.[post?.type]?.unit}</span>
          </p>
        </div>
      </div>
      <AccordionComponent data={getPostQuestions(post)} />
      <AccordionComponent data={getPostSellerDetails(post)} />
    </>
  );
};

const ContentPostCard = ({
  post = {},
  setShowImageSlider,
  setImageIndex = () => {},
  showImageSlider = false,
  imageIndex = 0,
  size = "large",
}) => {
  const mediaItems = post?.content?.media?.map((item) => ({
    key: item,
    name: item,
  }));

  return (
    <>
      <div className={classes.newPostDetails}>
        <ShowMoreShowLessText text={post?.content?.text} visibility={200} />
        <br />
        {post?.content?.media?.length > 0 && (
          <div className={classes.cardFooter}>
            {mediaItems?.length > 0 ? (
              <MediaComponent
                size={size}
                media={mediaItems}
                onShowMore={(e) => {
                  setShowImageSlider(true);
                  setImageIndex(e);
                }}
              />
            ) : (
              <NoData text="No attachments found" />
            )}
          </div>
        )}
      </div>

      {showImageSlider && (
        <ImageSliderModal
          show={showImageSlider}
          setShow={setShowImageSlider}
          gallery={mediaItems}
          imageIndex={imageIndex}
          setImageIndex={setImageIndex}
        />
      )}
    </>
  );
};

// ==================== Engagement Metrics Component ====================
const EngagementMetrics = ({ post, handleComment }) => {
  const [modal, setModal] = useState(null);
  return (
    <div className={classes.engagementMetrics}>
      <div className={classes.metricsSummary}>
        <div className={classes.metricsSummaryLeft}>
          <span
            onClick={() => {
              if (post?.likeCount > 0) {
                setModal("likes");
              }
            }}
            className={post?.likeCount > 0 ? "cursor" : ""}
          >
            {" "}
            {post?.likeCount} Likes
          </span>
          <div className={classes.metricsSeparator}></div>
          <span onClick={handleComment}>{post?.commentCount} Comments</span>
        </div>
        <div className={classes.metricsSeparator}></div>
        <span
          onClick={() => {
            if (post?.shareCount > 0) {
              setModal("shares");
            }
          }}
          className={post?.shareCount > 0 ? "cursor" : ""}
        >
          {post?.shareCount} Shares
        </span>
      </div>
      {["likes", "shares"].includes(modal) && (
        <UsersModal
          show={["likes", "shares"].includes(modal)}
          setShow={setModal}
          url={
            modal == "likes"
              ? `post-likes/find/${post?._id}`
              : `posts/find-shares/${post?._id}`
          }
          header={modal === "likes" ? "Likes" : "Shares"}
        />
      )}
    </div>
  );
};

// ==================== Main PostCard Component ====================
const PostCard = ({
  post,
  hideAllActions = false,
  size = "large",
  handleComment,
}) => {
  const [imageIndex, setImageIndex] = useState(0);
  const [showImageSlider, setShowImageSlider] = useState(false);

  const handleLocationClick = () => {
    const coords = post?.content?.location?.coordinates;
    if (coords?.length === 2) {
      const [lng, lat] = coords;
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
        "_blank"
      );
    }
  };

  const renderCard = (type) => {
    switch (type) {
      case POST_TYPE.CONSUMER:
        return <ConsumerPostCard post={post} />;
      case POST_TYPE.PRODUCER:
        return <ProducerPostCard post={post} />;
      case POST_TYPE.NEW_POST:
        return (
          <ContentPostCard
            post={post}
            setShowImageSlider={setShowImageSlider}
            setImageIndex={setImageIndex}
            showImageSlider={showImageSlider}
            imageIndex={imageIndex}
            size={size}
          />
        );
      default:
        return null;
    }
  };

  if (!post)
    return (
      <div className={classes.deletedPost}>
        <p className={classes.deletedPostText}>This post has been deleted</p>
      </div>
    );
  return (
    <div className={[classes.post, classes[size]].join(" ")}>
      <div className={classes.postHeader}>
        <UserDetailComponent
          size={size}
          data={{
            title: post?.isAnonymous ? "Anonymous" : post?.user?.fullName,
            description: formatDateTime(post?.createdAt),
            image: post?.isAnonymous ? "default.png" : post?.user?.image,
          }}
          isPro={post?.postTo == "exclusive"}
        />

        <div className={classes.postHeaderRight}>
          {post?.type === POST_TYPE.CONSUMER && (
            <div className={classes.tag}>
              <HiArrowDownTray size={18} />
              Consumer
            </div>
          )}

          {post?.type === POST_TYPE.PRODUCER && (
            <div className={classes.tag}>
              <HiArrowUpTray size={18} />
              Producer
            </div>
          )}

          {post?.type === POST_TYPE.NEW_POST &&
            post?.content?.location?.coordinates?.length > 0 && (
              <div
                onClick={handleLocationClick}
                className={classes.locationIcon}
              >
                <FaLocationDot size={18} />
              </div>
            )}
        </div>
      </div>

      {renderCard(post?.type)}

      {!hideAllActions && handleComment && (
        <EngagementMetrics post={post} handleComment={handleComment} />
      )}
    </div>
  );
};

// ==================== PostCardWrapper Component ====================
const PostCardWrapper = ({ post, hideAllActions = false }) => {
  const isSharePost = post?.type === POST_TYPE.SHARE;

  const sharedPost = isSharePost
    ? post?.share?.post
      ? { ...post?.share?.post, parentType: post?.type }
      : null
    : post;
  const [showCommentSection, setShowCommentSection] = useState(false);

  return (
    <div className={classes.post}>
      {isSharePost && (
        <div className={classes.postHeader}>
          <UserDetailComponent
            data={{
              title: post?.isAnonymous ? "Anonymous" : post?.user?.fullName,
              description: formatDateTime(post?.createdAt),
              image: post?.isAnonymous ? "default.png" : post?.user?.image,
            }}
            isPro={false}
          />
        </div>
      )}

      {isSharePost && post?.share?.text && (
        <div className={classes.sharePostText}>
          <ShowMoreShowLessText text={post?.share?.text} visibility={200} />
        </div>
      )}

      <div className={isSharePost ? classes.sharePostDetails : ""}>
        <PostCard
          size={isSharePost ? "small" : "large"}
          hideAllActions={isSharePost ? true : hideAllActions}
          post={sharedPost}
          handleComment={
            sharedPost && (() => setShowCommentSection((prev) => !prev))
          }
        />
      </div>

      {isSharePost && !hideAllActions && (
        <EngagementMetrics
          post={post}
          handleComment={() => setShowCommentSection((prev) => !prev)}
        />
      )}
      {showCommentSection && <CommentSection postId={post?._id} />}
    </div>
  );
};

export default PostCardWrapper;
